import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import userStore from '@/store/admin/userStore';
import LockIcon from '@mui/icons-material/Lock';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    LinearProgress,
    Grid,
    IconButton,
    Tooltip,
    Paper,
    Divider,
    Stack,
    useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MemoryIcon from '@mui/icons-material/Memory';
import StorageIcon from '@mui/icons-material/Storage';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SpeedIcon from '@mui/icons-material/Speed';
import DnsIcon from '@mui/icons-material/Dns';
import { Title } from 'react-admin';
import { useAuthMenu } from '@/services/authService/authMenu';
import {
    fetchActuatorJson,
    fetchContainerStats,
    MONITOR_SERVICES,
    type ServiceConfig,
} from '@/services/manageService/containerMonitorService';
import { canAccessServiceMonitor } from '@/utils/serviceMonitorAccess';

const BASE_URL = (import.meta.env.VITE_APP_BASE_API as string | undefined) || '/api';

interface ServiceData {
    name: string;
    key: string;
    color: string;
    status: 'UP' | 'DOWN' | 'UNKNOWN';
    heapUsed?: number;
    heapMax?: number;
    nonHeapUsed?: number;
    threads?: number;
    uptime?: number;
    containerMemoryUsed?: number;
    containerMemoryLimit?: number;
    cpuPercent?: number;
    components?: Record<string, { status: string }>;
    error?: string;
}

const REFRESH_INTERVAL = 30000;

function formatBytes(bytes: number | null | undefined): string {
    if (bytes == null || Number.isNaN(bytes)) return '-';
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatUptime(seconds: number | null | undefined): string {
    if (!seconds) return '-';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

function formatCpuPercent(pct: number | null | undefined): string {
    if (pct == null || Number.isNaN(pct)) return '-';
    return `${pct.toFixed(1)}%`;
}

async function fetchMetric(base: string, name: string): Promise<number | null> {
    try {
        const data = await fetchActuatorJson(`${base}/actuator/metrics/${name}`) as {
            measurements?: Array<{ value?: number }>;
        };
        return data.measurements?.[0]?.value ?? null;
    } catch {
        return null;
    }
}

async function fetchServiceData(svc: ServiceConfig): Promise<ServiceData> {
    const base = `${BASE_URL}/${svc.key}`;
    const result: ServiceData = { name: svc.name, key: svc.key, color: svc.color, status: 'UNKNOWN' };

    try {
        const health = await fetchActuatorJson(`${base}/actuator/health`, 5000, true) as {
            status?: string;
            components?: Record<string, { status: string }>;
        };
        result.status = health.status === 'UP' ? 'UP' : 'DOWN';
        result.components = health.components || {};

        const [heapUsed, heapMax, nonHeapUsed, threads, uptime, container] = await Promise.all([
            fetchMetric(base, 'jvm.memory.used?tag=area:heap'),
            fetchMetric(base, 'jvm.memory.max?tag=area:heap'),
            fetchMetric(base, 'jvm.memory.used?tag=area:nonheap'),
            fetchMetric(base, 'jvm.threads.live'),
            fetchMetric(base, 'process.uptime'),
            fetchContainerStats(svc.key),
        ]);

        result.heapUsed = heapUsed ?? undefined;
        result.heapMax = heapMax ?? undefined;
        result.nonHeapUsed = nonHeapUsed ?? undefined;
        result.threads = threads ?? undefined;
        result.uptime = uptime ?? undefined;

        if (container?.available) {
            result.containerMemoryUsed = container.memoryUsed;
            result.containerMemoryLimit = container.memoryLimit ?? undefined;
            result.cpuPercent = container.cpuPercent ?? undefined;
        }
    } catch (e: unknown) {
        result.status = 'DOWN';
        const err = e as { name?: string; message?: string };
        result.error = err.name === 'AbortError' ? 'Connection timeout' : (err.message || 'Unknown error');
    }

    return result;
}

function getProgressColor(pct: number): 'success' | 'warning' | 'error' {
    if (pct > 85) return 'error';
    if (pct > 70) return 'warning';
    return 'success';
}

function MemoryBar({
    label,
    icon,
    used,
    max,
    emptyLabel,
}: {
    label: string;
    icon: ReactNode;
    used?: number;
    max?: number;
    emptyLabel?: string;
}) {
    const hasUsed = used != null;
    const hasMax = max != null && max > 0;
    const pct = hasUsed && hasMax ? Math.min(100, Math.round(used / max * 100)) : 0;

    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {icon} {label}
                </Typography>
                <Typography variant="caption" fontWeight={600} color={
                    !hasUsed ? 'text.disabled' : pct > 85 ? 'error.main' : pct > 70 ? 'warning.main' : 'text.primary'
                }>
                    {hasUsed && hasMax ? `${pct}%` : (emptyLabel ?? '-')}
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={hasUsed && hasMax ? pct : 0}
                color={hasUsed && hasMax ? getProgressColor(pct) : 'inherit'}
                sx={{ height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3, display: 'block' }}>
                {hasUsed
                    ? `${formatBytes(used)}${hasMax ? ` / ${formatBytes(max)}` : ''}`
                    : (emptyLabel ?? 'N/A')}
            </Typography>
        </Box>
    );
}

function ServiceCard({ data }: { data: ServiceData }) {
    const theme = useTheme();
    const isUp = data.status === 'UP';
    const hasJvm = data.heapUsed != null;
    const hasContainer = data.containerMemoryUsed != null || data.cpuPercent != null;
    const showMetrics = hasJvm || hasContainer;
    const cpuPct = data.cpuPercent != null ? Math.min(100, Math.round(data.cpuPercent)) : 0;

    return (
        <Card
            sx={{
                height: '100%',
                borderLeft: `4px solid ${data.color}`,
                transition: 'box-shadow 0.3s',
                '&:hover': { boxShadow: theme.shadows[6] },
            }}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: data.color }}>
                        {data.name}
                    </Typography>
                    <Chip
                        icon={isUp ? <CheckCircleIcon /> : <ErrorIcon />}
                        label={data.status}
                        size="small"
                        color={isUp ? 'success' : 'error'}
                        variant="filled"
                    />
                </Box>

                {showMetrics && (
                    <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <MemoryBar
                                label="Heap Memory"
                                icon={<MemoryIcon sx={{ fontSize: 14 }} />}
                                used={data.heapUsed}
                                max={data.heapMax}
                                emptyLabel="N/A"
                            />
                            <MemoryBar
                                label="Container Memory"
                                icon={<DnsIcon sx={{ fontSize: 14 }} />}
                                used={data.containerMemoryUsed}
                                max={data.containerMemoryLimit}
                                emptyLabel={data.containerMemoryUsed == null ? 'N/A' : undefined}
                            />
                        </Stack>

                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <SpeedIcon sx={{ fontSize: 14 }} /> CPU (docker stats)
                                </Typography>
                                <Typography variant="caption" fontWeight={600} color={
                                    data.cpuPercent == null ? 'text.disabled'
                                        : cpuPct > 85 ? 'error.main'
                                            : cpuPct > 70 ? 'warning.main'
                                                : 'text.primary'
                                }>
                                    {formatCpuPercent(data.cpuPercent)}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={data.cpuPercent == null ? 0 : Math.min(100, data.cpuPercent)}
                                color={data.cpuPercent == null ? 'inherit' : getProgressColor(cpuPct)}
                                sx={{ height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3, display: 'block' }}>
                                {data.cpuPercent == null
                                    ? 'N/A (host / no cgroup)'
                                    : `${data.cpuPercent.toFixed(1)}% of one CPU core`}
                            </Typography>
                        </Box>

                        <Grid container spacing={1}>
                            <Grid size={{ xs: 4 }}>
                                <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                                    <StorageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="caption" display="block" color="text.secondary">Non-Heap</Typography>
                                    <Typography variant="body2" fontWeight={600}>{formatBytes(data.nonHeapUsed)}</Typography>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                                <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                                    <AccountTreeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="caption" display="block" color="text.secondary">Threads</Typography>
                                    <Typography variant="body2" fontWeight={600}>{data.threads ?? '-'}</Typography>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                                <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                                    <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="caption" display="block" color="text.secondary">Uptime</Typography>
                                    <Typography variant="body2" fontWeight={600}>{formatUptime(data.uptime)}</Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Stack>
                )}

                {data.components && Object.keys(data.components).length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 1 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                            Components
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {Object.entries(data.components)
                                .filter(([k]) => k !== 'ping')
                                .map(([name, info]) => (
                                    <Chip
                                        key={name}
                                        label={name}
                                        size="small"
                                        variant="outlined"
                                        color={info.status === 'UP' ? 'success' : 'error'}
                                        sx={{ fontSize: '0.7rem', height: 22 }}
                                    />
                                ))}
                        </Stack>
                    </Box>
                )}

                {data.error && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                        {data.error}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

const ServiceMonitor = observer(() => {
    const [services, setServices] = useState<ServiceData[]>([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

    const user = userStore.user;
    const { data: menuTree = [] } = useAuthMenu();
    const canAccess = canAccessServiceMonitor(user, menuTree);
    /** Skip overlapping polls so the 30s silent refresh does not stack. */
    const refreshInFlightRef = useRef(false);

    /**
     * Reload all service cards.
     *
     * @param silent when true, keep current cards (no progress bar / icon spin)
     */
    const refresh = useCallback(async (silent = false) => {
        if (!canAccess) return;
        if (refreshInFlightRef.current) return;
        refreshInFlightRef.current = true;
        // 1. First load / manual click may show loading; interval must not
        if (!silent) {
            setLoading(true);
        }
        try {
            const results = await Promise.all(MONITOR_SERVICES.map(fetchServiceData));
            setServices(results);
            setLastUpdate(new Date());
        } finally {
            if (!silent) {
                setLoading(false);
            }
            refreshInFlightRef.current = false;
        }
    }, [canAccess]);

    useEffect(() => {
        void refresh(false);
        const timer = setInterval(() => {
            void refresh(true);
        }, REFRESH_INTERVAL);
        return () => clearInterval(timer);
    }, [refresh]);

    if (!canAccess) {
        return (
            <Box sx={{ p: 3, textAlign: 'center', mt: 8 }}>
                <Title title="Service Monitor" />
                <LockIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                    Access Denied
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    This page is restricted to the superadmin account and sys-management role.
                </Typography>
            </Box>
        );
    }

    const upCount = services.filter(s => s.status === 'UP').length;
    const totalCount = services.length;

    return (
        <Box sx={{ p: 3 }}>
            <Title title="Service Monitor" />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Service Monitor
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {upCount}/{totalCount} services healthy
                        {lastUpdate && ` · Last updated: ${lastUpdate.toLocaleTimeString()}`}
                    </Typography>
                </Box>
                <Tooltip title="Refresh now">
                    <IconButton onClick={() => { void refresh(false); }} color="primary" disabled={loading}>
                        <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

            <Grid container spacing={2}>
                {services.map(svc => (
                    <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={svc.key}>
                        <ServiceCard data={svc} />
                    </Grid>
                ))}
            </Grid>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block', textAlign: 'center' }}>
                Auto-refresh every {REFRESH_INTERVAL / 1000}s · Container Memory / CPU from cgroup (same as docker stats)
            </Typography>
        </Box>
    );
});

export default ServiceMonitor;
